import { eventEmitter } from './event-emitter';
import { iframeManager } from './iframe-manager';
import { logger } from './logger';
import { messageBridge } from './message-bridge';
import { stateManager } from './state-manager';
import { CHAT_APP_URLS } from '../types/config';
import { MESSAGE_TYPES } from '../types/message';
import type {
  AICXClientContext,
  AICXConfig,
  AICXDeliveryAddressEditData,
  AICXDeliveryAddressEditRequest,
  SaaSWidgetDisplayConfig,
} from '../types/config';
import type { AICXChatStatusPayload, AICXSessionRequiredPayload } from '../types/event';
import type { AICXInstance } from '../types/instance';

const MAX_OPEN_URL_LENGTH = 8192;
const MAX_DOWNLOAD_FILE_NAME_LENGTH = 255;
const MAX_DELIVERY_ADDRESS_FIELD_LENGTH = 500;
const MAX_CLIENT_CONTEXT_ENTRIES = 16;
const MAX_CLIENT_CONTEXT_KEY_LENGTH = 64;
const MAX_CLIENT_CONTEXT_VALUE_LENGTH = 512;
const CLIENT_CONTEXT_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const ALLOWED_NEW_CONTEXT_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:', 'ohouse:']);
const UUID_SERVICE_KEY_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface NormalizedAppOpenUrlPayload {
  url: string;
  target: '_blank' | '_self';
  download: boolean;
  fileName?: string;
}

function readOwnDataProperty(value: unknown, key: string): unknown {
  try {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
    const descriptor = Reflect.getOwnPropertyDescriptor(value, key);
    return descriptor && 'value' in descriptor ? descriptor.value : undefined;
  } catch {
    return undefined;
  }
}

function isAllowedOpenUrl(url: string, target: '_blank' | '_self', download: boolean): boolean {
  try {
    const resolved = new URL(url, window.location.href);
    if (resolved.username || resolved.password) return false;

    if (download || target === '_self') {
      return resolved.protocol === 'http:' || resolved.protocol === 'https:';
    }

    return ALLOWED_NEW_CONTEXT_PROTOCOLS.has(resolved.protocol);
  } catch {
    return false;
  }
}

function normalizeAppOpenUrlPayload(input: unknown): NormalizedAppOpenUrlPayload | undefined {
  const rawUrl = readOwnDataProperty(input, 'url');
  if (typeof rawUrl !== 'string') return undefined;
  const url = rawUrl.trim();
  if (!url || url.length > MAX_OPEN_URL_LENGTH) return undefined;

  const rawTarget = readOwnDataProperty(input, 'target');
  if (rawTarget !== undefined && rawTarget !== '_blank' && rawTarget !== '_self') return undefined;
  const target = rawTarget ?? '_blank';

  const rawDownload = readOwnDataProperty(input, 'download');
  if (rawDownload !== undefined && typeof rawDownload !== 'boolean') return undefined;
  const download = rawDownload ?? false;

  const rawFileName = readOwnDataProperty(input, 'fileName');
  if (rawFileName !== undefined && typeof rawFileName !== 'string') return undefined;
  const fileName = rawFileName?.trim();
  if (fileName && fileName.length > MAX_DOWNLOAD_FILE_NAME_LENGTH) return undefined;

  if (!isAllowedOpenUrl(url, target, download)) return undefined;

  return {
    url,
    target,
    download,
    ...(fileName ? { fileName } : {}),
  };
}

function readOwnString(
  value: unknown,
  key: string,
  required = true,
  allowEmpty = false
): string | undefined {
  const rawValue = readOwnDataProperty(value, key);
  if (rawValue === undefined && !required) return undefined;
  if (typeof rawValue !== 'string') return undefined;
  const normalized = rawValue.trim();
  if ((!normalized && !allowEmpty) || normalized.length > MAX_DELIVERY_ADDRESS_FIELD_LENGTH) {
    return undefined;
  }
  return normalized;
}

function normalizeDeliveryAddressRequest(
  input: unknown
): (AICXDeliveryAddressEditRequest & { requestId: string }) | undefined {
  const requestId = readOwnString(input, 'requestId');
  const orderId = readOwnString(input, 'orderId');
  const bundleId = readOwnString(input, 'bundleId');
  const orderOptionId = readOwnString(input, 'orderOptionId', false);
  if (!requestId || !orderId || !bundleId) return undefined;
  return { requestId, orderId, bundleId, ...(orderOptionId ? { orderOptionId } : {}) };
}

function normalizeDeliveryAddress(input: unknown): AICXDeliveryAddressEditData | undefined {
  const recipient = readOwnString(input, 'recipient');
  const receivedPhoneNumber = readOwnString(input, 'receivedPhoneNumber');
  const zipCode = readOwnString(input, 'zipCode');
  const address = readOwnString(input, 'address');
  const addressDetail = readOwnString(input, 'addressDetail', true, true);
  const deliveryMemo = readOwnString(input, 'deliveryMemo', true, true);
  if (
    !recipient ||
    !receivedPhoneNumber ||
    !zipCode ||
    !address ||
    addressDetail === undefined ||
    deliveryMemo === undefined
  ) {
    return undefined;
  }
  return { recipient, receivedPhoneNumber, zipCode, address, addressDetail, deliveryMemo };
}

function readDisplayProperty(value: unknown, key: string): unknown {
  try {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
    if (!Reflect.getOwnPropertyDescriptor(value, key)) return undefined;
    return Reflect.get(value, key);
  } catch {
    return undefined;
  }
}

function readDisplayString(value: unknown, key: string): string | undefined {
  const property = readDisplayProperty(value, key);
  return typeof property === 'string' ? property : undefined;
}

function readDisplayBoolean(value: unknown, key: string): boolean | undefined {
  const property = readDisplayProperty(value, key);
  return typeof property === 'boolean' ? property : undefined;
}

function readDisplayPrimaryTextColor(value: unknown): '#000000' | '#FFFFFF' | undefined {
  return value === '#000000' || value === '#FFFFFF' ? value : undefined;
}

function readInquiryGuideChoiceStyle(value: unknown): 'soft' | 'brand-outline' | undefined {
  return value === 'soft' || value === 'brand-outline' ? value : undefined;
}

function readInquiryGuideChoiceLayout(value: unknown): 'wrap' | 'stack' | undefined {
  return value === 'wrap' || value === 'stack' ? value : undefined;
}

function withDisplayStrings<T extends Record<string, string | undefined>>(
  values: T
): Partial<T> | undefined {
  const entries = Object.entries(values).filter((entry): entry is [string, string] => {
    return entry[1] !== undefined;
  });
  return entries.length > 0 ? (Object.fromEntries(entries) as Partial<T>) : undefined;
}

function snapshotSaaSWidgetDisplayConfig(input: unknown): SaaSWidgetDisplayConfig | undefined {
  const rawBrand = readDisplayProperty(input, 'brand');
  const brand = withDisplayStrings({
    name: readDisplayString(rawBrand, 'name'),
    logoUrl: readDisplayString(rawBrand, 'logoUrl'),
    primaryColor: readDisplayString(rawBrand, 'primaryColor'),
    primaryTextColor: readDisplayPrimaryTextColor(
      readDisplayProperty(rawBrand, 'primaryTextColor')
    ),
  });

  const rawHome = readDisplayProperty(input, 'home');
  const rawAnnouncement = readDisplayProperty(rawHome, 'announcement');
  const announcement = withDisplayStrings({
    title: readDisplayString(rawAnnouncement, 'title'),
    content: readDisplayString(rawAnnouncement, 'content'),
  });
  const greeting = readDisplayString(rawHome, 'greeting');
  const home = {
    ...(greeting !== undefined ? { greeting } : {}),
    ...(announcement ? { announcement } : {}),
  };

  const rawLinks = readDisplayProperty(input, 'links');
  const links = withDisplayStrings({
    notice: readDisplayString(rawLinks, 'notice'),
    faq: readDisplayString(rawLinks, 'faq'),
    privacy: readDisplayString(rawLinks, 'privacy'),
    terms: readDisplayString(rawLinks, 'terms'),
  });

  const rawConversation = readDisplayProperty(input, 'conversation');
  const rawInquiryGuide = readDisplayProperty(rawConversation, 'inquiryGuide');
  const inquiryGuide = withDisplayStrings({
    choiceStyle: readInquiryGuideChoiceStyle(readDisplayProperty(rawInquiryGuide, 'choiceStyle')),
    choiceLayout: readInquiryGuideChoiceLayout(
      readDisplayProperty(rawInquiryGuide, 'choiceLayout')
    ),
  });
  const showNewInquiry = readDisplayBoolean(rawConversation, 'showNewInquiry');
  const conversation = {
    ...(showNewInquiry !== undefined ? { showNewInquiry } : {}),
    ...(inquiryGuide ? { inquiryGuide } : {}),
  };

  const snapshot: SaaSWidgetDisplayConfig = {
    ...(brand ? { brand } : {}),
    ...(Object.keys(home).length > 0 ? { home } : {}),
    ...(links ? { links } : {}),
    ...(Object.keys(conversation).length > 0 ? { conversation } : {}),
  };
  return Object.keys(snapshot).length > 0 ? snapshot : undefined;
}

function snapshotClientContext(input: unknown): AICXClientContext | undefined {
  if (input === undefined) return undefined;
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    throw new Error('[AICX SDK] clientContext는 문자열 key/value 객체여야 합니다.');
  }
  const keys = Object.keys(input);
  if (keys.length > MAX_CLIENT_CONTEXT_ENTRIES) {
    throw new Error(
      `[AICX SDK] clientContext는 최대 ${MAX_CLIENT_CONTEXT_ENTRIES}개까지 허용됩니다.`
    );
  }
  const result: AICXClientContext = Object.create(null) as AICXClientContext;
  for (const key of keys) {
    const value = readOwnDataProperty(input, key);
    if (
      key.length > MAX_CLIENT_CONTEXT_KEY_LENGTH ||
      !CLIENT_CONTEXT_KEY_PATTERN.test(key) ||
      typeof value !== 'string' ||
      !value.trim() ||
      Array.from(value).length > MAX_CLIENT_CONTEXT_VALUE_LENGTH
    ) {
      throw new Error('[AICX SDK] clientContext의 key/value 형식이 올바르지 않습니다.');
    }
    result[key] = value;
  }
  return keys.length > 0 ? result : undefined;
}

function copyConfigWithoutDisplayGetter(config: AICXConfig): AICXConfig {
  const copy = Object.create(null) as AICXConfig;
  for (const key of Object.keys(config) as Array<keyof AICXConfig>) {
    if (key === 'saasWidgetDisplayConfig' || key === 'clientContext') continue;
    Object.defineProperty(copy, key, {
      value: Reflect.get(config, key),
      enumerable: true,
      configurable: true,
      writable: true,
    });
  }
  return copy;
}

/**
 * AICX SDK 메인 클래스
 */
class AICXClass {
  /** ready 전에 호출된 sendMessage 대기열 */
  private pendingMessages: string[] = [];

  /** chat-app ready 상태 */
  private isReady: boolean = false;

  /** chat-app에서 수신한 채팅방 ID */
  private chatId: string | null = null;

  private static readonly CHAT_ID_STORAGE_PREFIX = 'aicx_chat_id';
  private static readonly ANON_USER_KEY = 'anon';
  private static readonly WIDGET_HIDE_DELAY_MS = 100;
  private readyTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private widgetHideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private messageUnsubscribers: Array<() => void> = [];
  private isSessionRefreshPending: boolean = false;
  private hasSentFirstMessage: boolean = false;
  private closedChatIds: Set<string> = new Set();
  private focusReturnTarget: HTMLElement | null = null;

  /**
   * SDK 초기화
   * 설정을 저장하고 인스턴스를 반환합니다.
   */
  init(config: AICXConfig): AICXInstance {
    const state = stateManager.getState();

    if (state.initialized) {
      logger.warn('SDK가 이미 초기화되었습니다. 기존 인스턴스를 반환합니다.');
      return this.createInstance();
    }

    if (!config.serviceKey) {
      throw new Error(
        '[AICX SDK] serviceKey는 필수입니다.\n' +
          "예: AICX.init({ serviceKey: 'your-service-key', environment: 'production' })"
      );
    }

    if (config.product !== undefined && config.product !== 'chatbot' && config.product !== 'aicc') {
      throw new Error("[AICX SDK] product는 'chatbot' 또는 'aicc'여야 합니다.");
    }
    if (
      config.surface !== undefined &&
      config.surface !== 'support-center' &&
      config.surface !== 'conversation'
    ) {
      throw new Error("[AICX SDK] surface는 'support-center' 또는 'conversation'이어야 합니다.");
    }
    if (config.surface !== undefined && config.product !== 'aicc') {
      throw new Error("[AICX SDK] surface는 product: 'aicc'에서만 사용할 수 있습니다.");
    }
    if (config.product === 'aicc' && !UUID_SERVICE_KEY_PATTERN.test(config.serviceKey)) {
      throw new Error('[AICX SDK] AICC 제품의 serviceKey는 UUID 형식이어야 합니다.');
    }

    // chatAppUrl 결정: 직접 지정 > environment > 에러
    const chatAppUrl = this.resolveChatAppUrl(config);

    try {
      logger.setEnabled(config.debug ?? false);
      const baseConfig = copyConfigWithoutDisplayGetter(config);
      const loggableConfig = { ...baseConfig, chatAppUrl };
      delete loggableConfig.aicxSessionToken;
      delete loggableConfig.preContext;
      delete loggableConfig.clientContext;
      delete loggableConfig.saasWidgetDisplayConfig;
      delete loggableConfig.deliveryAddressProvider;
      logger.log('SDK 초기화 시작', loggableConfig);
      this.isSessionRefreshPending = false;
      this.hasSentFirstMessage = false;
      this.closedChatIds.clear();
      this.focusReturnTarget = null;

      // postMessage에 전달할 표시 설정은 init 시점의 plain string allowlist로 고정한다.
      // 잘못된 nested 값이나 이후 외부 mutation이 SDK_INIT 전체를 깨뜨리지 않게 한다.
      const displayConfig = snapshotSaaSWidgetDisplayConfig(
        readDisplayProperty(config, 'saasWidgetDisplayConfig')
      );
      const clientContext = snapshotClientContext(readOwnDataProperty(config, 'clientContext'));
      const resolvedConfig = {
        ...baseConfig,
        chatAppUrl,
        ...(displayConfig ? { saasWidgetDisplayConfig: displayConfig } : {}),
        ...(clientContext ? { clientContext } : {}),
      };
      stateManager.setConfig(resolvedConfig);
      stateManager.setInitialized(true);
      this.chatId = this.resolveChatId(resolvedConfig);

      logger.log('SDK 초기화 완료');
      return this.createInstance();
    } catch (error) {
      logger.error('SDK 초기화 중 에러 발생:', error);
      throw error;
    }
  }

  /**
   * chat-app URL 결정
   * 우선순위: chatAppUrl > environment > 에러
   */
  private resolveChatAppUrl(config: AICXConfig): string {
    let baseUrl: string | undefined;

    // 1. chatAppUrl 직접 지정된 경우
    if (config.chatAppUrl) {
      baseUrl = config.chatAppUrl;
    }

    // 2. environment로 프리셋 URL 사용
    if (!baseUrl && config.environment) {
      const url = CHAT_APP_URLS[config.environment];
      if (url) {
        baseUrl = url;
      }
    }

    if (!baseUrl) {
      throw new Error(
        '[AICX SDK] environment 또는 chatAppUrl 중 하나는 필수입니다.\n' +
          "예: AICX.init({ environment: 'production' }) 또는 AICX.init({ chatAppUrl: 'http://localhost:5173' })"
      );
    }

    if (config.product !== 'aicc') return baseUrl;

    try {
      const url = new URL(baseUrl);
      if (
        (url.protocol !== 'http:' && url.protocol !== 'https:') ||
        url.username ||
        url.password ||
        url.search ||
        url.hash
      ) {
        throw new Error('invalid AICC chatAppUrl');
      }
      const pathname = url.pathname.replace(/\/+$/, '');
      if (pathname && pathname !== '/widget') {
        throw new Error('unsupported AICC chatAppUrl pathname');
      }
      url.pathname = '/widget';
      return url.toString().replace(/\/$/, '');
    } catch {
      throw new Error('[AICX SDK] AICC 제품의 chatAppUrl은 유효한 HTTP(S) URL이어야 합니다.');
    }
  }

  private extractChatIdFromChatUrl(chatUrl: string | undefined): string | null {
    if (!chatUrl?.trim()) {
      return null;
    }

    try {
      const parsed = new URL(chatUrl);
      const match = parsed.pathname.match(/\/chat\/([^/?#]+)/);
      return match?.[1]?.trim() || null;
    } catch {
      return null;
    }
  }

  private resolveChatId(config: AICXConfig): string | null {
    const explicitChatId = config.chatId?.trim() || this.extractChatIdFromChatUrl(config.chatUrl);
    if (explicitChatId) {
      this.persistChatId(explicitChatId, config);
      return explicitChatId;
    }

    return this.loadPersistedChatId(config);
  }

  /**
   * AICXInstance 객체 생성
   */
  private createInstance(): AICXInstance {
    return {
      open: () => {
        this.open();
        return this.createInstance();
      },
      close: () => {
        this.close();
        return this.createInstance();
      },
      destroy: () => {
        this.destroy();
      },
      isOpen: () => this.isOpen(),
      on: (event, handler) => eventEmitter.on(event, handler),
      off: (event, handler) => eventEmitter.off(event, handler),
      sendMessage: (message) => this.sendMessage(message),
      setTheme: (theme) => this.setTheme(theme),
      ping: () => this.ping(),
      resetChatSession: () => this.resetChatSession(),
      updateSession: (aicxSessionToken) => this.updateSession(aicxSessionToken),
      clearSession: () => this.clearSession(),
      updateContext: (context) => this.updateContext(context),
    };
  }

  /** APP_READY 대기 타임아웃 (ms) */
  private static readonly READY_TIMEOUT = 30000;

  private getChatIdStorageKey(config: AICXConfig): string {
    const productScope = config.product === 'aicc' ? ':aicc' : '';
    return `${AICXClass.CHAT_ID_STORAGE_PREFIX}${productScope}:${config.serviceKey}:${config.userId ?? AICXClass.ANON_USER_KEY}`;
  }

  private getAnonStorageKey(config: AICXConfig): string {
    const productScope = config.product === 'aicc' ? ':aicc' : '';
    return `${AICXClass.CHAT_ID_STORAGE_PREFIX}${productScope}:${config.serviceKey}:${AICXClass.ANON_USER_KEY}`;
  }

  private canPersistChatId(config: AICXConfig): boolean {
    // AICC 회원 token은 불투명하고 userId가 필수가 아니다. 이 경우 공용 anon
    // key를 쓰면 브라우저의 이전 계정 대화가 다음 계정에 복원될 수 있다.
    return !(config.product === 'aicc' && config.aicxSessionToken && !config.userId);
  }

  private loadPersistedChatId(config: AICXConfig): string | null {
    if (typeof window === 'undefined' || !this.canPersistChatId(config)) return null;

    try {
      const key = this.getChatIdStorageKey(config);
      const current = window.localStorage.getItem(key);
      if (current?.trim()) return current;

      // userId가 새로 부여된 경우, anon 세션 chatId를 승격해 연속성을 유지합니다.
      if (config.userId) {
        const anonKey = this.getAnonStorageKey(config);
        const anonChatId = window.localStorage.getItem(anonKey);
        if (anonChatId?.trim()) {
          window.localStorage.setItem(key, anonChatId);
          return anonChatId;
        }
      }

      return null;
    } catch (error) {
      logger.warn('chatId localStorage 조회 실패', error);
      return null;
    }
  }

  private persistChatId(chatId: string, configOverride?: AICXConfig): void {
    if (!chatId.trim() || typeof window === 'undefined') return;

    const config = configOverride ?? stateManager.getState().config;
    if (!config?.serviceKey || !this.canPersistChatId(config)) return;

    try {
      const key = this.getChatIdStorageKey(config);
      window.localStorage.setItem(key, chatId);
    } catch (error) {
      logger.warn('chatId localStorage 저장 실패', error);
    }
  }

  private clearReadyTimeout(): void {
    if (!this.readyTimeoutId) return;
    clearTimeout(this.readyTimeoutId);
    this.readyTimeoutId = null;
  }

  private clearWidgetHideTimeout(): void {
    if (this.widgetHideTimeoutId === null) return;
    clearTimeout(this.widgetHideTimeoutId);
    this.widgetHideTimeoutId = null;
  }

  private clearMessageSubscriptions(): void {
    if (this.messageUnsubscribers.length === 0) return;
    for (const unsubscribe of this.messageUnsubscribers) {
      unsubscribe();
    }
    this.messageUnsubscribers = [];
  }

  private sendSDKInit(config: AICXConfig, resetChatSession = false): void {
    const displayConfig = readDisplayProperty(config, 'saasWidgetDisplayConfig') as
      | SaaSWidgetDisplayConfig
      | undefined;
    messageBridge.send(MESSAGE_TYPES.SDK_INIT, {
      theme: config.theme ?? 'light',
      debug: config.debug ?? false,
      origin: window.location.origin,
      hostPageUrl: window.location.href,
      serviceKey: config.serviceKey,
      ...(config.product === 'aicc' && config.surface && { surface: config.surface }),
      ...(config.chatAppUrl && { chatAppPathname: new URL(config.chatAppUrl).pathname }),
      ...(config.aicxSessionToken && { aicxSessionToken: config.aicxSessionToken }),
      ...(config.environment && { environment: config.environment }),
      ...(config.userId && { userId: config.userId }),
      ...(config.userKey && { userKey: config.userKey }),
      ...(config.journeyId && { journeyId: config.journeyId }),
      ...(config.botName && { botName: config.botName }),
      ...(config.botProfileImage && { botProfileImage: config.botProfileImage }),
      ...(config.preContext !== undefined && { preContext: config.preContext }),
      ...(!this.chatId &&
        config.clientContext !== undefined && {
          clientContext: config.clientContext,
        }),
      ...(config.initialBotMessage && { initialBotMessage: config.initialBotMessage }),
      ...(config.chatBackgroundColor && { chatBackgroundColor: config.chatBackgroundColor }),
      ...(config.botBubbleBackgroundColor && {
        botBubbleBackgroundColor: config.botBubbleBackgroundColor,
      }),
      ...(config.userBubbleBackgroundColor && {
        userBubbleBackgroundColor: config.userBubbleBackgroundColor,
      }),
      ...(displayConfig !== undefined && {
        saasWidgetDisplayConfig: displayConfig,
      }),
      ...(this.chatId && { chatId: this.chatId }),
      ...(config.product === 'aicc' && {
        widgetVisibilityEvents: true as const,
        unreadMessageIntegration: true as const,
      }),
      ...(resetChatSession && { resetChatSession: true as const }),
    });
  }

  private sendWidgetVisibility(visible: boolean): void {
    messageBridge.send(MESSAGE_TYPES.SDK_WIDGET_VISIBILITY_CHANGED, { visible });
  }

  private normalizeChatIdPayload(payload: unknown): string | null {
    const value = readOwnDataProperty(payload, 'chatId');
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private handleChatIdChanged(payload: unknown): void {
    const chatId = this.normalizeChatIdPayload(payload);
    if (!chatId) {
      logger.warn('APP_CHAT_ID_CHANGED payload 형식이 올바르지 않습니다.');
      return;
    }
    if (chatId === this.chatId) return;
    this.chatId = chatId;
    this.persistChatId(chatId);
    eventEmitter.emit('conversation:changed', { conversationId: chatId });
    logger.log('채팅방 ID 수신', { chatId });
  }

  private handleUnreadRefreshRequired(payload: unknown): void {
    const chatId = this.normalizeChatIdPayload(payload);
    const reason = readOwnDataProperty(payload, 'reason');
    if (!chatId || chatId !== this.chatId || reason !== 'read') {
      logger.warn('APP_UNREAD_REFRESH_REQUIRED payload 형식이 올바르지 않습니다.');
      return;
    }
    eventEmitter.emit('unread:refresh-required', {
      conversationId: chatId,
      reason,
    });
  }

  private resolveRuntimeDisplayConfig(config: AICXConfig): AICXConfig {
    const resolvedConfig: AICXConfig = {
      ...config,
    };

    if (config.initialBotMessage !== undefined && config.initialBotMessage !== '') {
      resolvedConfig.initialBotMessage = config.initialBotMessage;
    }

    return resolvedConfig;
  }

  private handleAppReady(config: AICXConfig): void {
    const resolvedConfig = this.resolveRuntimeDisplayConfig(config);
    stateManager.setConfig(resolvedConfig);
    this.sendSDKInit(resolvedConfig);
    this.isReady = true;
    this.flushPendingMessages();
    eventEmitter.emit('ready');
    if (this.chatId) {
      eventEmitter.emit('conversation:changed', { conversationId: this.chatId });
    }
    this.sendWidgetVisibility(stateManager.getState().isOpen);
  }

  private createDownloadAnchor(url: string, fileName?: string): HTMLAnchorElement {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.style.display = 'none';
    anchor.download = fileName?.trim() || '';
    return anchor;
  }

  /**
   * 첨부파일 다운로드 요청 처리
   *
   * chat-app은 iframe 내부에서 실행되므로 sandbox 정책에 따라 a[download]가 막힐 수 있다.
   * SDK 부모 window에서 attachment proxy URL을 한 번만 클릭해 중복 저장을 방지한다.
   */
  private handleDownloadUrl(url: string, fileName?: string): void {
    const anchor = this.createDownloadAnchor(url, fileName);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  private handleOpenUrl(
    url: string,
    target: '_blank' | '_self' = '_blank',
    options: { download?: boolean; fileName?: string } = {}
  ): void {
    if (!url) return;

    if (options.download) {
      this.handleDownloadUrl(url, options.fileName);
      return;
    }
    if (target === '_self') {
      window.location.assign(url);
      return;
    }

    // `noopener`가 지정된 window.open은 새 탭이 정상 생성돼도 표준상 null을 반환한다.
    // 반환값을 팝업 차단으로 해석해 anchor fallback을 실행하면 같은 URL이 두 번 열린다.
    // 사용자 클릭에서 전달된 새 탭 요청은 noopener anchor 한 번으로만 처리한다.
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  private normalizeChatStatus(status: string): 'open' | 'closed' {
    const normalized = status.trim().toLowerCase();
    if (
      normalized === 'closed' ||
      normalized === 'chatbot_closed' ||
      normalized.endsWith('_closed')
    ) {
      return 'closed';
    }
    return 'open';
  }

  private isValidChatStatusPayload(payload: unknown): payload is {
    chatId: string;
    status: string;
    closeReason?: string | null;
  } {
    if (!payload || typeof payload !== 'object') return false;
    const maybePayload = payload as { chatId?: unknown; status?: unknown };
    return (
      typeof maybePayload.chatId === 'string' &&
      !!maybePayload.chatId.trim() &&
      typeof maybePayload.status === 'string'
    );
  }

  private handleChatStatus(payload: unknown): void {
    if (!this.isValidChatStatusPayload(payload)) {
      logger.warn('APP_CHAT_STATUS payload 형식이 올바르지 않습니다.', payload);
      return;
    }

    const normalizedPayload: AICXChatStatusPayload = {
      chatId: payload.chatId.trim(),
      status: this.normalizeChatStatus(payload.status),
      closeReason: payload.closeReason ?? null,
    };

    eventEmitter.emit('chat:status', normalizedPayload);

    if (normalizedPayload.status === 'closed') {
      if (!this.closedChatIds.has(normalizedPayload.chatId)) {
        this.closedChatIds.add(normalizedPayload.chatId);
        eventEmitter.emit('chat:closed', normalizedPayload);
      }
      return;
    }

    this.closedChatIds.delete(normalizedPayload.chatId);
  }

  private isValidSessionRequiredPayload(payload: unknown): payload is AICXSessionRequiredPayload {
    if (!payload || typeof payload !== 'object') return false;
    const reason = (payload as { reason?: unknown }).reason;
    return reason === 'expired' || reason === 'missing' || reason === 'invalid';
  }

  private handleSessionRequired(payload: unknown): void {
    if (!this.isValidSessionRequiredPayload(payload)) {
      logger.warn('APP_SESSION_REQUIRED payload 형식이 올바르지 않습니다.');
      return;
    }

    if (this.isSessionRefreshPending) return;
    this.isSessionRefreshPending = true;
    eventEmitter.emit('session:required', payload);
  }

  /**
   * 챗봇 열기
   */
  private open(): void {
    this.ensureInitialized();

    const { isOpen } = stateManager.getState();
    if (isOpen) {
      logger.warn('챗봇이 이미 열려있습니다.');
      return;
    }

    logger.log('챗봇 열기');
    this.clearWidgetHideTimeout();

    const activeElement = typeof document === 'undefined' ? null : document.activeElement;
    this.focusReturnTarget =
      activeElement && activeElement !== document.body && 'focus' in activeElement
        ? (activeElement as HTMLElement)
        : null;

    // iframe 로드 실패 시 error 이벤트 발행
    iframeManager.setOnError((error) => {
      eventEmitter.emit('error', error);
    });

    iframeManager.show();
    stateManager.setIsOpen(true);
    eventEmitter.emit('open');

    // MessageBridge 연결
    const { iframe, config } = stateManager.getState();
    if (iframe && config?.chatAppUrl) {
      // URL에서 origin 추출 (예: 'https://chat.aicx.kr/path' → 'https://chat.aicx.kr')
      const origin = new URL(config.chatAppUrl).origin;

      if (!messageBridge.getIsConnected()) {
        messageBridge.connect(iframe, origin);
      }

      this.clearMessageSubscriptions();

      // chat-app이 준비되면 초기 설정 전송
      const unsubscribeReady = messageBridge.subscribe(MESSAGE_TYPES.APP_READY, () => {
        this.clearReadyTimeout();
        const latestConfig = stateManager.getState().config;
        if (latestConfig) {
          void this.handleAppReady(latestConfig);
        }
      });

      // chat-app에서 채팅방 ID 변경 알림 수신
      const unsubscribeChatId = messageBridge.subscribe(
        MESSAGE_TYPES.APP_CHAT_ID_CHANGED,
        (payload) => this.handleChatIdChanged(payload)
      );

      const unsubscribeUnreadRefresh = messageBridge.subscribe(
        MESSAGE_TYPES.APP_UNREAD_REFRESH_REQUIRED,
        (payload) => this.handleUnreadRefreshRequired(payload)
      );

      // chat-app에서 닫기 요청이 오면 처리
      const unsubscribeClose = messageBridge.subscribe(MESSAGE_TYPES.APP_CLOSE_REQUEST, () => {
        this.close();
      });

      const unsubscribeChatStatus = messageBridge.subscribe(
        MESSAGE_TYPES.APP_CHAT_STATUS,
        (payload) => {
          this.handleChatStatus(payload);
        }
      );

      // chat-app에서 외부 URL 열기 요청 (Android WebView iframe 대응)
      const unsubscribeOpenUrl = messageBridge.subscribe(MESSAGE_TYPES.APP_OPEN_URL, (payload) => {
        const request = normalizeAppOpenUrlPayload(payload);
        if (!request) return;
        this.handleOpenUrl(request.url, request.target, request);
      });

      const unsubscribeSessionRequired = messageBridge.subscribe(
        MESSAGE_TYPES.APP_SESSION_REQUIRED,
        (payload) => {
          this.handleSessionRequired(payload);
        }
      );

      const unsubscribeChatResetRequest = messageBridge.subscribe(
        MESSAGE_TYPES.APP_CHAT_RESET_REQUEST,
        () => this.resetChatSession()
      );

      const unsubscribeDeliveryAddress = messageBridge.subscribe(
        MESSAGE_TYPES.APP_DELIVERY_ADDRESS_REQUEST,
        (payload) => {
          const request = normalizeDeliveryAddressRequest(payload);
          if (!request) return;

          const provider = stateManager.getState().config?.deliveryAddressProvider;
          if (!provider) {
            messageBridge.send(MESSAGE_TYPES.SDK_DELIVERY_ADDRESS_RESPONSE, {
              requestId: request.requestId,
              error: 'not_configured',
            });
            return;
          }

          void provider({
            orderId: request.orderId,
            bundleId: request.bundleId,
            ...(request.orderOptionId ? { orderOptionId: request.orderOptionId } : {}),
          })
            .then((value) => {
              const address = normalizeDeliveryAddress(value);
              messageBridge.send(MESSAGE_TYPES.SDK_DELIVERY_ADDRESS_RESPONSE, {
                requestId: request.requestId,
                ...(address ? { address } : { error: 'not_found' as const }),
              });
            })
            .catch(() => {
              messageBridge.send(MESSAGE_TYPES.SDK_DELIVERY_ADDRESS_RESPONSE, {
                requestId: request.requestId,
                error: 'failed',
              });
            });
        }
      );

      this.messageUnsubscribers = [
        unsubscribeReady,
        unsubscribeChatId,
        unsubscribeUnreadRefresh,
        unsubscribeClose,
        unsubscribeChatStatus,
        unsubscribeOpenUrl,
        unsubscribeSessionRequired,
        unsubscribeChatResetRequest,
        unsubscribeDeliveryAddress,
      ];

      // 이미 ready 상태인 iframe을 재오픈할 때는 APP_READY 대기/타임아웃을 건너뜁니다.
      if (this.isReady) {
        const latestConfig = stateManager.getState().config;
        if (latestConfig) {
          this.sendSDKInit(latestConfig);
        }
        this.sendWidgetVisibility(true);
        this.flushPendingMessages();
        return;
      }

      // APP_READY 수신 타임아웃 (30초 내 응답 없으면 error 이벤트)
      this.clearReadyTimeout();
      this.readyTimeoutId = setTimeout(() => {
        eventEmitter.emit('error', new Error('chat-app 연결 타임아웃 (30초)'));
        logger.error('chat-app 연결 타임아웃');
      }, AICXClass.READY_TIMEOUT);
    }
  }

  /**
   * 챗봇 닫기
   */
  private close(): void {
    this.ensureInitialized();

    const { isOpen } = stateManager.getState();
    if (!isOpen) {
      logger.warn('챗봇이 이미 닫혀있습니다.');
      return;
    }

    logger.log('챗봇 닫기');
    // APP_READY 대기 중 닫히는 경우, 잔여 타임아웃으로 가짜 에러가 발생하지 않게 정리
    this.clearReadyTimeout();
    stateManager.setIsOpen(false);
    this.sendWidgetVisibility(false);
    // iframe이 보이는 동안 chat-app이 닫힘 상태와 분석 이벤트를 처리할 한 tick을 보장한다.
    // 즉시 display:none으로 전환하면 숨은 iframe의 timer/network 작업이 지연될 수 있다.
    this.clearWidgetHideTimeout();
    this.widgetHideTimeoutId = setTimeout(() => {
      this.widgetHideTimeoutId = null;
      iframeManager.hide();
    }, AICXClass.WIDGET_HIDE_DELAY_MS);

    const focusReturnTarget = this.focusReturnTarget;
    this.focusReturnTarget = null;
    if (focusReturnTarget?.isConnected) focusReturnTarget.focus();

    eventEmitter.emit('close');
  }

  /**
   * SDK 완전 정리
   */
  private destroy(): void {
    if (!stateManager.getState().initialized) {
      logger.warn('SDK가 초기화되지 않았습니다.');
      return;
    }

    logger.log('SDK 정리 시작');
    this.clearReadyTimeout();
    this.clearWidgetHideTimeout();
    this.clearMessageSubscriptions();
    iframeManager.destroy();
    messageBridge.destroy();
    stateManager.reset();
    this.isReady = false;
    this.isSessionRefreshPending = false;
    this.pendingMessages = [];
    this.chatId = null;
    this.closedChatIds.clear();
    this.focusReturnTarget = null;
    logger.log('SDK 정리 완료');

    eventEmitter.removeAllListeners();
  }

  /**
   * 테마를 동적으로 변경합니다
   */
  private setTheme(theme: 'light' | 'dark'): void {
    this.ensureInitialized();

    const { config } = stateManager.getState();
    if (!config) return;

    stateManager.setConfig({ ...config, theme });
    messageBridge.send(MESSAGE_TYPES.SDK_THEME_CHANGE, { theme });
    logger.log('테마 변경', { theme });
  }

  /**
   * chat-app에 채팅 메시지 전송
   * ready 전이면 대기열에 추가, ready 후면 즉시 전송
   */
  private sendMessage(message: string): void {
    this.ensureInitialized();

    const config = stateManager.getState().config;
    const strategy = config?.firstMessageStrategy ?? 'use_input';
    const configuredFirstMessage = config?.firstMessage?.trim() ?? '';
    const inputMessage = message.trim();
    const resolvedMessage =
      !this.hasSentFirstMessage && strategy === 'use_configured' && configuredFirstMessage
        ? configuredFirstMessage
        : inputMessage;

    if (!resolvedMessage) {
      logger.warn('빈 메시지는 전송할 수 없습니다.');
      return;
    }

    this.hasSentFirstMessage = true;

    if (this.isReady) {
      messageBridge.send(MESSAGE_TYPES.SDK_SEND_MESSAGE, {
        message: resolvedMessage,
      });
      logger.log('메시지 전송', { messageLength: resolvedMessage.length, strategy });
    } else {
      this.pendingMessages.push(resolvedMessage);
      logger.log('메시지 대기열에 추가 (ready 대기 중)', {
        messageLength: resolvedMessage.length,
        strategy,
      });
    }
  }

  /**
   * 대기 중인 메시지를 모두 전송
   */
  private flushPendingMessages(): void {
    if (this.pendingMessages.length === 0) return;

    logger.log(`대기 메시지 ${this.pendingMessages.length}건 전송`);
    for (const message of this.pendingMessages) {
      messageBridge.send(MESSAGE_TYPES.SDK_SEND_MESSAGE, { message });
    }
    this.pendingMessages = [];
  }

  /**
   * chat-app 연결 상태 확인
   * SDK_PING을 보내고 APP_PONG 응답을 대기합니다 (3초 타임아웃)
   */
  private ping(): Promise<boolean> {
    return new Promise((resolve) => {
      let unsubscribe = (): void => undefined;
      const timeout = setTimeout(() => {
        unsubscribe();
        resolve(false);
      }, 3000);

      unsubscribe = messageBridge.subscribe(MESSAGE_TYPES.APP_PONG, () => {
        clearTimeout(timeout);
        unsubscribe();
        resolve(true);
      });

      messageBridge.send(MESSAGE_TYPES.SDK_PING);
    });
  }

  /**
   * 챗봇 열림 상태 확인
   */
  private isOpen(): boolean {
    return stateManager.getState().isOpen;
  }

  /**
   * 초기화 여부 확인 공통 로직
   */
  private ensureInitialized(): void {
    if (!stateManager.getState().initialized) {
      const errorMsg = 'SDK가 초기화되지 않았습니다. AICX.init()을 먼저 호출하세요.';
      logger.error(errorMsg);
      throw new Error(`[AICX SDK] ${errorMsg}`);
    }
  }

  private resetChatSession(notifyIframe = true): void {
    this.ensureInitialized();

    const { config } = stateManager.getState();
    if (!config || typeof window === 'undefined') {
      this.chatId = null;
      return;
    }

    if (this.canPersistChatId(config)) {
      const currentKey = this.getChatIdStorageKey(config);
      const anonKey = this.getAnonStorageKey(config);

      try {
        window.localStorage.removeItem(currentKey);
        if (anonKey !== currentKey) {
          window.localStorage.removeItem(anonKey);
        }
      } catch (error) {
        logger.warn('chatId localStorage 삭제 실패', error);
      }
    }

    this.chatId = null;
    this.pendingMessages = [];
    this.hasSentFirstMessage = false;
    this.closedChatIds.clear();
    if (notifyIframe && this.isReady) {
      this.sendSDKInit(config, true);
    }
    logger.log('채팅 세션 리셋');
  }

  private updateSession(aicxSessionToken: string): void {
    this.ensureInitialized();

    if (!aicxSessionToken.trim()) {
      throw new Error('[AICX SDK] aicxSessionToken은 빈 문자열일 수 없습니다.');
    }

    const { config } = stateManager.getState();
    if (!config) return;

    stateManager.setConfig({ ...config, aicxSessionToken });
    this.isSessionRefreshPending = false;

    if (this.isReady) {
      messageBridge.send(MESSAGE_TYPES.SDK_SESSION_UPDATE, {
        aicxSessionToken,
      });
    }
  }

  private clearSession(): void {
    this.ensureInitialized();

    const { config } = stateManager.getState();
    if (!config) return;

    this.resetChatSession(false);
    const nextConfig = { ...config };
    delete nextConfig.aicxSessionToken;
    delete nextConfig.preContext;
    delete nextConfig.clientContext;
    stateManager.setConfig(nextConfig);
    this.isSessionRefreshPending = false;

    if (this.isReady) {
      messageBridge.send(MESSAGE_TYPES.SDK_SESSION_CLEAR);
    }
  }

  private updateContext(context: { userId?: string; preContext?: string }): void {
    this.ensureInitialized();

    const { config } = stateManager.getState();
    if (!config) return;

    const nextConfig: AICXConfig = {
      ...config,
      ...(context.userId !== undefined ? { userId: context.userId } : {}),
      ...(context.preContext !== undefined ? { preContext: context.preContext } : {}),
    };

    stateManager.setConfig(nextConfig);
    this.chatId = this.resolveChatId(nextConfig);

    if (this.chatId) {
      this.persistChatId(this.chatId);
    }

    if (this.isReady) {
      this.sendSDKInit(nextConfig);
    }
  }
}

/** SDK 싱글톤 인스턴스 */
export const AICX = new AICXClass();
